import { useEffect } from 'react';
import { APP_STORE_URL, PLAY_STORE_URL } from '../config/storeLinks';

/**
 * Milliseconds to wait for the app to open before falling back to the store.
 * The visibilitychange event fires within ~300 ms on iOS when the custom scheme
 * launches the native app. 2 500 ms gives ample margin without feeling slow.
 */
const APP_OPEN_DELAY_MS = 2500;

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}

interface PostRedirectProps {
  postId?: string;
  profileUsername?: string;
}

/**
 * Intercepts `/post/:postId` URLs and attempts to open the native I'd Go Back
 * app via the `idgoback://` custom scheme. Falls back to the App Store or
 * Play Store if the app is not installed.
 *
 * Flow:
 *  1. Validate the postId is a UUID — reject bad IDs with a 404-like view.
 *  2. Fire `idgoback://post/:postId` immediately after mount.
 *  3. Listen for `visibilitychange`: if the browser goes hidden the app
 *     opened successfully — cancel the fallback timer.
 *  4. If APP_OPEN_DELAY_MS elapses without `visibilitychange`, assume the
 *     app is not installed and redirect to the appropriate store.
 */
export function PostRedirect({ postId, profileUsername }: PostRedirectProps) {
  const isProfileRedirect = Boolean(profileUsername);
  const isValidUUID = postId ? UUID_REGEX.test(postId) : false;
  const isValidUsername = profileUsername ? /^[a-zA-Z0-9._-]{3,30}$/.test(profileUsername) : false;
  const isValidRedirectTarget = isProfileRedirect ? isValidUsername : isValidUUID;
  const deepLinkPath = isProfileRedirect
    ? `profile/${encodeURIComponent(profileUsername ?? '')}`
    : `post/${postId ?? ''}`;

  useEffect(() => {
    if (!isValidRedirectTarget) return;

    let appOpened = false;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Attempt to hand off to the native app via custom scheme.
    window.location.href = `idgoback://${deepLinkPath}`;

    const timer = setTimeout(() => {
      if (!appOpened) {
        // App not installed — send the user to the appropriate store.
        if (isIOS()) {
          window.location.href = APP_STORE_URL;
        } else if (isAndroid()) {
          window.location.href = PLAY_STORE_URL;
        } else {
          // Desktop or unrecognised platform — go back to the landing page.
          window.location.href = '/';
        }
      }
    }, APP_OPEN_DELAY_MS);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [deepLinkPath, isValidRedirectTarget]);

  if (!isValidRedirectTarget) {
    return (
      <div className="flex flex-col items-center justify-center min-h-dynamic-screen gap-4 px-6 text-center">
        <p className="text-xl font-semibold">
          {isProfileRedirect ? 'Profile not found' : 'Post not found'}
        </p>
        <p className="text-sm text-gray-500">This link may have expired or been removed.</p>
        <a
          href="/"
          className="mt-2 text-sm font-medium text-primary underline underline-offset-4"
        >
          Go to I'd Go Back
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-dynamic-screen gap-4 px-6 text-center">
      <p className="text-xl font-semibold">Opening I'd Go Back…</p>
      <p className="text-sm text-gray-500">
        If the app does not open, you will be redirected to download it.
      </p>
    </div>
  );
}
